import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import Staff from './Staff';
import Project from './Project';
import Address from './Address';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';
import {required} from '@vuelidate/validators';

export default class ProjectContract extends BaseModel {
  constructor({
    id,
    number,
    designation,
    project = undefined,
    staff = undefined,
    address = undefined,
    beginDate = undefined,
    endDate = undefined,
    lastDate = undefined,
    memo = undefined,
    billingAmount = 0,
    billingDescription = undefined,
    billingFrequency = undefined,
    indexDefinition = undefined,
    workFrequency = undefined,
    interventionDaysBefore = 0,
    status = undefined,
    billingType = undefined,
  }) {
    super(id, number, designation);
    this.project = project ? new Project(project) : undefined;
    this.staff = staff ? new Staff(staff) : undefined;
    this.address = address ? new Address(address) : undefined;
    this.beginDate = beginDate;
    this.endDate = endDate;
    this.lastDate = lastDate;
    this.memo = memo;
    this.billingAmount = billingAmount;
    this.billingDescription = billingDescription;
    this.billingFrequency = billingFrequency;
    this.indexDefinition = indexDefinition;
    this.workFrequency = workFrequency;
    this.interventionDaysBefore = interventionDaysBefore;
    this.status = status
      ? HitOnlineEnums.getEnumValue('HIT_CONTRACT_STATUS', status, translate)
      : null;
    this.billingType = billingType
      ? HitOnlineEnums.getEnumValue('HIT_BILLING_TYPE', billingType, translate)
      : null;
  }

  static get baseModuleForRedirection() {
    return 'project-base';
  }

  static get defaultSort() {
    return {
      property: 'number',
      order: 'asc',
    };
  }

  getDefaultSort() {
    return ProjectContract.defaultSort;
  }

  static get printTemplateType() {
    return 'projectContract';
  }
  get printTemplateType() {
    return ProjectContract.printTemplateType;
  }

  static get config() {
    return {
      addable: true,
      duplicatable: true,
      importable: false,
      browsable: true,
      deletable: true,
    };
  }
  get config() {
    return ProjectContract.config;
  }

  static get createInitColumns() {
    return {
      designation: '',
      intervention_days_before: 0,
      status: 'CONTRACT_ACTIVE',
    };
  }
  get createInitColumns() {
    return ProjectContract.createInitColumns;
  }

  static get fkName() {
    return 'project_contract_id';
  }
  static get apiRoute() {
    return 'project_contract';
  }
  static get hasEntityStaffRolesBulkUpdate() {
    return true;
  }
  static get detailPageRoute() {
    return 'projectContractDetail';
  }
  static get listPageRoute() {
    return 'projectContractDetail';
  }
  static get joinFilterColumns() {
    return ['search'];
  }
  get fkName() {
    return ProjectContract.fkName;
  }
  get apiRoute() {
    return ProjectContract.apiRoute;
  }
  get listPageRoute() {
    return ProjectContract.listPageRoute;
  }
  get detailPageRoute() {
    return ProjectContract.detailPageRoute;
  }
  get attrsToStoreInForm() {
    return BaseModel.attrsToStoreInForm;
  }
  get joinFilterColumns() {
    return ProjectContract.joinFilterColumns;
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'designation',
      'project',
      'staff',
      'address',
      'beginDate',
      'endDate',
      'lastDate',
      'billingDescription',
      'billingAmount',
      'status',
      'billingType',
      'tags',
    ]);
  }

  static get entityListDisplayAttrs() {
    return this.getAttributes([
      'designation',
      'address',
      'staff',
      'workFrequency',
      'beginDate',
      'endDate',
      'lastDate',
      'billingAmount',
      'status',
      'tags',
    ]);
  }

  static get joinAttrsKey() {
    return [
      'number',
      'designation',
      'project',
      'address',
      'staff',
      'status',
      'billingType',
      'billingAmount',
      'billingDescription',
      'billingFrequency',
      'indexDefinition',
      'workFrequency',
      'interventionDaysBefore',
    ];
  }

  static get browserDisplayAttrs() {
    return this.getAttributes([
      'project',
      'number',
      'designation',
      'staff',
      'status',
      'billingType',
      'billingAmount',
      'billingDescription',
      'billingFrequency',
      'indexDefinition',
      'workFrequency',
      'interventionDaysBefore',
    ]);
  }

  static get positionInformationAttrs() {
    return this.getAttributes(
      [],
      [
        'project',
        'number',
        'designation',
        'staff',
        'status',
        'billingType',
        'billingAmount',
        'billingDescription',
        'billingFrequency',
        'indexDefinition',
        'workFrequency',
      ],
    );
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'number',
        'designation',
        'staff',
        'project',
        'status',
        'billingType',
        'billingAmount',
        'billingDescription',
        'billingFrequency',
        'indexDefinition',
        'workFrequency',
      ]),
      ...this.entityStaffRolesBulkUpdateAttrs,
    };
  }
  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE(),
      this.DESIGNATION_ATTRIBUTE(),
      new AT.FkProjectAttribute({
        name: 'project',
        validations: {required},
        joinPath: ['project!fk_project_contract_project_id_project'],
        tableProperties: {
          header: 'hit-base.common.project',
        },
      }),
      new AT.FkStaffAttribute({
        name: 'staff',
        joinPath: ['staff!fk_project_contract_staff_id_staff'],
        tableProperties: {
          header: 'hit-app.project-contract.responsible',
          maxWidth: '1fr',
        },
      }),
      new AT.FkAddressAttribute({
        name: 'address',
        validations: {required},
        joinPath: ['address!fk_project_contract_address_id_address'],
        tableProperties: {
          header: 'hit-app.project-contract.address',
        },
      }),
      new AT.DateAttribute({
        column: 'begin_date',
        name: 'beginDate',
        tableProperties: {
          header: 'hit-app.common.begin-date',
        },
      }),
      new AT.DateAttribute({
        column: 'end_date',
        name: 'endDate',
        tableProperties: {
          header: 'hit-app.common.end-date',
        },
      }),
      new AT.DateAttribute({
        column: 'last_date',
        name: 'lastDate',
        tableProperties: {
          header: 'hit-app.project-contract.last-date',
        },
      }),
      this.MEMO_RICH_TEXT_ATTRIBUTE(),
      new AT.DecimalAttribute({
        name: 'billingAmount',
        column: 'billing_amount',
        tableProperties: {
          header: 'hit-app.project-contract.billing-amount',
        },
        decimalPrecision: 2,
      }),
      new AT.StringAttribute({
        name: 'billingDescription',
        column: 'billing_description',
        tableProperties: {
          header: 'hit-app.project-contract.billing-description',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'billingFrequency',
        column: 'billing_frequency',
        tableProperties: {
          header: 'hit-app.project-contract.billing-frequency',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'indexDefinition',
        column: 'index_definition',
        tableProperties: {
          header: 'hit-app.project-contract.index-definition',
        },
      }),
      new AT.JsonObjectAttribute({
        name: 'workFrequency',
        column: 'work_frequency',
        tableProperties: {
          header: 'hit-app.project-contract.work-frequency',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'status',
        enumType: 'HIT_CONTRACT_STATUS',
        enumClass: HitOnlineEnums,
        tableProperties: {
          propertyName: 'status',
          header: 'hit-app.project-contract.status',
          maxWidth: '1.5fr',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'billingType',
        column: 'billing_type',
        enumType: 'HIT_BILLING_TYPE',
        enumClass: HitOnlineEnums,
        tableProperties: {
          propertyName: 'billingType',
          header: 'hit-app.project-contract.billing-type',
        },
      }),
      new AT.IntegerAttribute({
        name: 'interventionDaysBefore',
        column: 'intervention_days_before',
        defaultValue: 0,
        tableProperties: {
          header: 'hit-app.project-contract.intervention-days-before',
        },
      }),
      this.TAGS_ATTRIBUTE({
        moduleUsage: 'project',
        tableOrigin: 'project_contract',
      }),
    ];
  }
}
